import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, Users, Target, MessageSquare, 
  Loader2, ArrowUpRight, Trophy, Briefcase 
} from 'lucide-react';
import { salesService } from '../../services/salesService';
import { useAuth } from '../../context/AuthContext';
import FaceAttendanceWidget from '../../components/FaceAttendanceWidget';

const SalesManagerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await salesService.getLeads();
        setLeads(res.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const getCount = (status) => leads.filter(l => l.status === status).length;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary w-10 h-10 opacity-20" /></div>;

  return (
    <div className="space-y-6 text-gray-900">
      {/* Header */}
      <div className="bg-white rounded-card border shadow-soft p-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-black tracking-tight">Sales Management</h1>
          <p className="text-gray-400 font-bold uppercase text-[10px] tracking-widest mt-1">Team Overview & Pipeline</p>
        </div>
        <div className="p-3 bg-primary/5 text-primary rounded-2xl"><Target className="w-6 h-6" /></div>
      </div>

      {/* Sales KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SalesStat label="Total Leads" value={leads.length} icon={<Briefcase />} color="text-primary" />
        <SalesStat label="Won Deals" value={getCount('Won')} icon={<Trophy />} color="text-green-600" />
        <SalesStat label="In Negotiation" value={getCount('Negotiation')} icon={<TrendingUp />} color="text-blue-600" />
        <SalesStat label="Meetings Set" value={getCount('Meeting Scheduled')} icon={<MessageSquare />} color="text-amber-600" />
      </div>

      <FaceAttendanceWidget />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Team Actions */}
        <div className="bg-white rounded-card border border-gray-100 shadow-soft p-6">
           <h2 className="text-xs font-black text-primary uppercase tracking-[0.2em] mb-6 border-b pb-4">Sales Operations</h2>
           <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ActionButton label="View All Leads" desc="Monitor team pipeline" onClick={() => navigate('/sales')} />
              <ActionButton label="Commission Rankings" desc="View top performers" onClick={() => navigate('/commissions')} />
              <ActionButton label="Recent Meetings" desc="Handoffs and results" onClick={() => navigate('/sales')} />
              <ActionButton label="New Opportunity" desc="Assign a direct lead" onClick={() => navigate('/sales/new')} />
           </div>
        </div>

        {/* Sales Tip/Quote */}
        <div className="bg-gradient-to-br from-indigo-600 to-primary text-white rounded-card p-8 flex flex-col justify-center relative overflow-hidden shadow-xl shadow-primary/20">
           <div className="relative z-10">
              <h2 className="text-2xl font-black mb-3">Goal Focus</h2>
              <p className="text-indigo-100 text-sm font-medium leading-relaxed">
                "Quality over Quantity. Focus on leads in the Negotiation phase to hit this month's targets."
              </p>
           </div>
           <Trophy className="absolute -right-6 -bottom-6 w-40 h-48 text-white opacity-10 -rotate-12" />
        </div>
      </div>
    </div>
  );
};

const SalesStat = ({ label, value, icon, color }) => (
  <div className="bg-white p-5 rounded-card border border-gray-100 shadow-soft flex items-center gap-4 hover:border-primary/20 transition-all group">
    <div className={'p-3 rounded-xl bg-gray-50 ' + color + ' group-hover:scale-110 transition-transform'}>{icon}</div>
    <div>
      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{label}</p>
      <p className="text-xl font-black text-gray-900 mt-0.5">{value}</p>
    </div>
  </div>
);

const ActionButton = ({ label, desc, onClick }) => (
  <button onClick={onClick} className="p-4 rounded-xl border border-gray-50 bg-gray-50/50 hover:bg-white hover:border-primary/30 hover:shadow-md transition-all text-left group">
    <p className="text-sm font-bold text-gray-900 flex items-center justify-between">
      {label} <ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-primary" />
    </p>
    <p className="text-[10px] text-gray-400 font-medium uppercase mt-1 tracking-tighter">{desc}</p>
  </button>
);

export default SalesManagerDashboard;